import { Scrollable } from '@affine/component';
import { PageDetailLoading } from '@affine/component/page-detail-skeleton';
import { type AIChatParams, AIProvider } from '@affine/core/blocksuite/ai';
import type { AffineEditorContainer } from '@affine/core/blocksuite/block-suite-editor';
import { EditorOutlineViewer } from '@affine/core/blocksuite/outline-viewer';
import { AffineErrorBoundary } from '@affine/core/components/affine/affine-error-boundary';
import { useGuard } from '@affine/core/components/guard';
import { PageNotFound } from '@affine/core/desktop/pages/404';
import { EditorService } from '@affine/core/modules/editor';
import {
  fitReadonlyEdgelessViewport,
  mountReadonlyEdgelessEdgeRuntime,
} from '@affine/core/modules/streamlain/edge-infrastructure/readonly-edge-runtime';
import { DebugLogger } from '@affine/debug';
import { DisposableGroup } from '@blocksuite/affine/global/disposable';
import { Bound, deserializeXYWH } from '@blocksuite/affine/global/gfx';
import { RefNodeSlotsProvider } from '@blocksuite/affine/inlines/reference';
import { GfxControllerIdentifier } from '@blocksuite/affine/std/gfx';
import type { GfxModel } from '@blocksuite/std/gfx';
import {
  FrameworkScope,
  useLiveData,
  useService,
  useServices,
} from '@toeverything/infra';
import clsx from 'clsx';
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { Subscription } from 'rxjs';

import { WorkbenchService } from '../../../workbench';
import type { DocReferenceInfo } from '../../entities/peek-view';
import { PeekViewService } from '../../services/peek-view';
import { useEditor } from '../utils';
import * as styles from './doc-peek-view.css';
import { getPreviewSearchHighlightRoots } from './preview-search-roots';

const logger = new DebugLogger('doc-peek-view');

// Lazy load BlockSuiteEditor to break circular dependency
const BlockSuiteEditor = lazy(() =>
  import('@affine/core/blocksuite/block-suite-editor').then(module => ({
    default: module.BlockSuiteEditor,
  }))
);

const previewViewportAnimationRuns = new WeakMap<object, number>();
const PREVIEW_SEARCH_HIGHLIGHT_NAME = 'streamlain-doc-peek-search-match';
const MAX_PREVIEW_SEARCH_HIGHLIGHTS = 100;

type HighlightRegistry = {
  set: (name: string, highlight: unknown) => void;
  delete: (name: string) => void;
};

type HighlightConstructor = new (...ranges: Range[]) => unknown;

function getPreviewSearchHighlightApi() {
  const registry = (
    globalThis.CSS as unknown as { highlights?: HighlightRegistry } | undefined
  )?.highlights;
  const HighlightCtor = (
    globalThis as unknown as {
      Highlight?: HighlightConstructor;
    }
  ).Highlight;

  return registry && HighlightCtor ? { registry, HighlightCtor } : null;
}

function clearPreviewSearchHighlight() {
  getPreviewSearchHighlightApi()?.registry.delete(
    PREVIEW_SEARCH_HIGHLIGHT_NAME
  );
}

function applyPreviewSearchHighlight(
  editor: AffineEditorContainer,
  target: PreviewFocusTarget,
  query: string
) {
  const normalizedQuery = query.trim();
  const api = getPreviewSearchHighlightApi();
  if (!api || !normalizedQuery) {
    clearPreviewSearchHighlight();
    return clearPreviewSearchHighlight;
  }

  const ranges: Range[] = [];
  const roots = getPreviewSearchHighlightRoots(editor, target);
  for (const root of roots) {
    ranges.push(
      ...createPreviewSearchHighlightRanges(
        root,
        normalizedQuery,
        MAX_PREVIEW_SEARCH_HIGHLIGHTS - ranges.length
      )
    );
    if (ranges.length >= MAX_PREVIEW_SEARCH_HIGHLIGHTS) break;
  }

  if (ranges.length === 0) {
    clearPreviewSearchHighlight();
    return clearPreviewSearchHighlight;
  }

  api.registry.set(
    PREVIEW_SEARCH_HIGHLIGHT_NAME,
    new api.HighlightCtor(...ranges)
  );
  return clearPreviewSearchHighlight;
}

function createPreviewSearchHighlightRanges(
  root: Element,
  query: string,
  limit: number
) {
  if (limit <= 0) return [];

  const ownerDocument = root.ownerDocument;
  const segments: Array<{ node: Text; start: number; end: number }> = [];
  let text = '';
  collectPreviewTextNodes(root, node => {
    const value = node.data;
    segments.push({
      node,
      start: text.length,
      end: text.length + value.length,
    });
    text += value;
  });

  const normalizedText = text.toLowerCase();
  const normalizedQuery = query.toLowerCase();
  const ranges: Range[] = [];
  let index = 0;

  while (ranges.length < limit) {
    const matchIndex = normalizedText.indexOf(normalizedQuery, index);
    if (matchIndex < 0) break;

    const range = createPreviewSearchRange(
      ownerDocument,
      segments,
      matchIndex,
      matchIndex + normalizedQuery.length
    );
    if (range) ranges.push(range);
    index = matchIndex + Math.max(normalizedQuery.length, 1);
  }

  return ranges;
}

function collectPreviewTextNodes(root: Node, visit: (node: Text) => void) {
  const ownerDocument = root.ownerDocument;
  if (!ownerDocument) return;

  const walker = ownerDocument.createTreeWalker(root, NodeFilter.SHOW_ALL);
  let current: Node | null = root;

  while (current) {
    if (current.nodeType === Node.TEXT_NODE) {
      const text = current as Text;
      if (!shouldSkipPreviewTextNode(text)) visit(text);
    } else if (current instanceof Element && current.shadowRoot) {
      collectPreviewTextNodes(current.shadowRoot, visit);
    }
    current = walker.nextNode();
  }
}

function shouldSkipPreviewTextNode(node: Text) {
  if (!node.data.trim()) return true;
  const parent = node.parentElement;
  if (!parent) return true;
  if (parent.closest('[hidden], [aria-hidden="true"]')) return true;
  return ['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE'].includes(parent.tagName);
}

function createPreviewSearchRange(
  document: Document,
  segments: Array<{ node: Text; start: number; end: number }>,
  start: number,
  end: number
) {
  const startSegment = segments.find(
    segment => start >= segment.start && start < segment.end
  );
  const endSegment = segments.find(
    segment => end > segment.start && end <= segment.end
  );
  if (!startSegment || !endSegment) return null;

  const range = document.createRange();
  range.setStart(startSegment.node, start - startSegment.start);
  range.setEnd(endSegment.node, end - endSegment.start);
  return range;
}

function fitViewport(
  editor: AffineEditorContainer,
  xywh?: `[${number},${number},${number},${number}]`
) {
  try {
    if (!editor.host) {
      throw new Error('editor host is not ready');
    }

    const gfx = editor.host.std.get(GfxControllerIdentifier);
    const viewport = gfx.viewport;
    viewport.onResize();

    if (xywh) {
      const newViewport = {
        xywh: xywh,
        padding: [60, 20, 20, 20] as [number, number, number, number],
      };
      viewport.setViewportByBound(
        Bound.deserialize(newViewport.xywh),
        newViewport.padding,
        false
      );
    } else {
      fitReadonlyEdgelessViewport(gfx, [0, 0, 0, 0]);
    }
  } catch (e) {
    logger.warn('failed to fitViewPort', e);
  }
}

type PreviewFocusTarget = Pick<
  DocReferenceInfo,
  | 'blockIds'
  | 'elementIds'
  | 'parentBlockId'
  | 'parentFlavour'
  | 'noteBlockId'
  | 'mode'
  | 'pageIndex'
  | 'query'
  | 'requestId'
  | 'xywh'
  | 'fitToContent'
> & {
  refreshKey: string;
};

function getPreviewFocusKey(target: PreviewFocusTarget) {
  return [
    target.mode ?? '',
    target.blockIds?.join(',') ?? '',
    target.elementIds?.join(',') ?? '',
    target.parentBlockId ?? '',
    target.parentFlavour ?? '',
    target.noteBlockId ?? '',
    target.pageIndex ?? '',
    target.query ?? '',
    target.refreshKey,
    target.xywh ?? '',
    target.fitToContent ? 'fit' : '',
  ].join('|');
}

function syncPreviewPdfNavigation(
  editor: AffineEditorContainer,
  target: PreviewFocusTarget
) {
  const blockId = target.blockIds?.length === 1 ? target.blockIds[0] : null;
  if (blockId && target.pageIndex !== undefined) {
    editor.setAttribute('data-streamlain-pdf-preview-block-id', blockId);
    editor.setAttribute(
      'data-streamlain-pdf-preview-page-index',
      String(target.pageIndex)
    );
    editor.setAttribute(
      'data-streamlain-pdf-preview-request-id',
      target.requestId ?? ''
    );
    return;
  }

  editor.removeAttribute('data-streamlain-pdf-preview-block-id');
  editor.removeAttribute('data-streamlain-pdf-preview-page-index');
  editor.removeAttribute('data-streamlain-pdf-preview-request-id');
}

function isPreviewNavigableBound(
  bounds: Bound | null | undefined
): bounds is Bound {
  return (
    !!bounds &&
    Number.isFinite(bounds.w) &&
    Number.isFinite(bounds.h) &&
    bounds.w > 0 &&
    bounds.h > 0
  );
}

function resolvePreviewAnchorBounds(
  candidates: Array<{ bounds: Bound | null | undefined }>
) {
  let bounds: Bound | null = null;

  for (const candidate of candidates) {
    const candidateBounds = candidate.bounds;
    if (!isPreviewNavigableBound(candidateBounds)) continue;
    bounds = bounds ? bounds.unite(candidateBounds) : candidateBounds.clone();
  }

  return bounds;
}

function getGfxModelBound(gfxElement: GfxModel | null | undefined) {
  if (!gfxElement) return null;
  if (
    'elementBound' in gfxElement &&
    gfxElement.elementBound instanceof Bound
  ) {
    return gfxElement.elementBound;
  }

  const xywh = (gfxElement as { xywh?: string }).xywh;
  return xywh ? Bound.fromXYWH(deserializeXYWH(xywh)) : null;
}

function getModelBound(model: unknown) {
  const xywh = getSerializedXYWH(model);
  if (!xywh) return null;

  try {
    return Bound.deserialize(xywh);
  } catch {
    return null;
  }
}

function getSerializedXYWH(model: unknown) {
  if (!model || typeof model !== 'object') return null;
  const direct = (model as { xywh?: unknown }).xywh;
  if (typeof direct === 'string' && direct.length > 0) return direct;

  const props = (model as { props?: { xywh?: unknown } }).props;
  const propsXYWH = props?.xywh;
  return typeof propsXYWH === 'string' && propsXYWH.length > 0
    ? propsXYWH
    : null;
}

function getBlockModelBound(editor: AffineEditorContainer, id: string) {
  return getModelBound(editor.host?.std.store.getBlock(id)?.model);
}

function getBlockComponentBound(
  editor: AffineEditorContainer,
  id: string,
  viewport: {
    zoom: number;
    toModelCoordFromClientCoord: (point: [number, number]) => [number, number];
  }
) {
  const blockComponent = editor.host?.std.view.getBlock(id);
  if (!blockComponent) return null;
  const rect = blockComponent.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return null;

  const [x, y] = viewport.toModelCoordFromClientCoord([rect.left, rect.top]);
  return new Bound(
    x,
    y,
    rect.width / viewport.zoom,
    rect.height / viewport.zoom
  );
}

function animatePreviewViewport(
  viewport: {
    centerX: number;
    centerY: number;
    zoom: number;
    setCenter: (x: number, y: number) => void;
    setZoom: (zoom: number, focusPoint?: { x: number; y: number }) => void;
  },
  target: { centerX: number; centerY: number; zoom: number }
) {
  const runId = (previewViewportAnimationRuns.get(viewport) ?? 0) + 1;
  previewViewportAnimationRuns.set(viewport, runId);

  const startX = viewport.centerX;
  const startY = viewport.centerY;
  const startZoom = viewport.zoom;
  const duration = 420;
  const start = performance.now();
  const ease = (t: number) =>
    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

  const step = (now: number) => {
    if (previewViewportAnimationRuns.get(viewport) !== runId) return;

    const progress = ease(Math.min((now - start) / duration, 1));
    const x = lerp(startX, target.centerX, progress);
    const y = lerp(startY, target.centerY, progress);
    const zoom = lerp(startZoom, target.zoom, progress);
    viewport.setCenter(x, y);
    viewport.setZoom(zoom, { x, y });

    if (progress < 1) {
      requestAnimationFrame(step);
    }
  };

  requestAnimationFrame(step);
}

function focusPreviewTarget(
  editor: AffineEditorContainer,
  target: PreviewFocusTarget
) {
  try {
    if (!editor.host) {
      throw new Error('editor host is not ready');
    }

    const mode = target.mode ?? 'page';
    if (mode === 'page') {
      const id = target.blockIds?.[0];
      if (!id) return false;
      const blockComponent = editor.host.std.view.getBlock(id);
      if (!blockComponent) return false;
      requestAnimationFrame(() => {
        blockComponent.scrollIntoView({
          behavior: 'instant',
          block: 'center',
        });
      });
      return true;
    }

    const gfx = editor.host.std.get(GfxControllerIdentifier);
    const viewport = gfx.viewport;
    viewport.onResize();

    if (target.xywh) {
      viewport.setViewportByBound(
        Bound.deserialize(target.xywh),
        [60, 20, 20, 20],
        false
      );
      return true;
    }

    const ids = target.elementIds?.length
      ? target.elementIds
      : (target.blockIds ?? []);
    const directBoundCandidates = ids.map(id => {
      const gfxElement = gfx.getElementById<GfxModel>(id);
      const bounds =
        getGfxModelBound(gfxElement) ??
        getBlockComponentBound(editor, id, viewport) ??
        getBlockModelBound(editor, id);
      return { bounds, id };
    });

    // A document-level Edgeless result has no concrete ids. Returning false
    // delegates to fitViewport(), which fits the complete document. Concrete
    // results never fall back to a parent Note: their own GFX/DOM/model
    // geometry is the navigation contract shared with the real open path.
    if (ids.length === 0) return false;
    const bounds = resolvePreviewAnchorBounds(directBoundCandidates);

    if (!isPreviewNavigableBound(bounds)) return false;

    const fit = viewport.getFitToScreenData(bounds, [20, 20, 100, 20]);
    animatePreviewViewport(viewport, fit);
    return true;
  } catch (e) {
    logger.warn('failed to focus preview target', e);
    return false;
  }
}

function focusOrFitPreviewTarget(
  editor: AffineEditorContainer,
  target: PreviewFocusTarget,
  xywh?: `[${number},${number},${number},${number}]`,
  resolvedMode?: DocReferenceInfo['mode']
) {
  if (
    !focusPreviewTarget(editor, target) &&
    ((target.mode ?? resolvedMode) === 'edgeless' ||
      (target.fitToContent && resolvedMode === 'edgeless'))
  ) {
    fitViewport(editor, xywh);
  }
}

function DocPeekPreviewEditor({
  focusTarget,
  hideReadOnlyPermissionBanner,
  searchQuery,
  xywh,
}: {
  focusTarget: PreviewFocusTarget;
  hideReadOnlyPermissionBanner?: boolean;
  searchQuery?: string;
  xywh?: `[${number},${number},${number},${number}]`;
}) {
  const { editorService } = useServices({
    EditorService,
  });
  const editor = editorService.editor;
  const doc = editor.doc;
  const workspace = editor.doc.workspace;
  const previewPage = useMemo(
    () =>
      doc.blockSuiteDoc.doc.getStore({
        readonly: true,
        extensions: doc.blockSuiteDoc.userExtensions,
      }),
    [doc.blockSuiteDoc]
  );
  const mode = useLiveData(editor.mode$);
  const defaultOpenProperty = useLiveData(editor.defaultOpenProperty$);
  const workbench = useService(WorkbenchService).workbench;
  const peekView = useService(PeekViewService).peekView;
  const [editorElement, setEditorElement] =
    useState<AffineEditorContainer | null>(null);
  const focusKey = getPreviewFocusKey(focusTarget);
  const hasFocusTarget = !!(
    focusTarget.blockIds?.length ||
    focusTarget.elementIds?.length ||
    focusTarget.xywh
  );
  const shouldFocusPreview =
    hasFocusTarget || focusTarget.fitToContent || mode === 'edgeless';

  const handleOnEditorReady = useCallback(
    (editorContainer: AffineEditorContainer) => {
      const disposableGroup = new DisposableGroup();
      editorContainer.setAttribute('data-streamlain-peek-preview', 'true');
      syncPreviewPdfNavigation(editorContainer, focusTarget);
      setEditorElement(editorContainer);
      const disposeEdges =
        mode === 'edgeless'
          ? mountReadonlyEdgelessEdgeRuntime({
              editor: editorContainer,
              store: previewPage,
            })
          : undefined;

      const refNodeSlots =
        editorContainer.std.getOptional(RefNodeSlotsProvider);
      if (refNodeSlots) {
        // doc change event inside peek view should be handled by peek view
        disposableGroup.add(
          // todo(@pengx17): seems not working
          refNodeSlots.docLinkClicked.subscribe(options => {
            if (options.host !== editorContainer.host) {
              return;
            }
            peekView
              .open({
                docRef: { docId: options.pageId },
                ...options.params,
              })
              .catch(console.error);
          })
        );
      }

      if (shouldFocusPreview) {
        requestAnimationFrame(() =>
          focusOrFitPreviewTarget(editorContainer, focusTarget, xywh, mode)
        );
      }

      return () => {
        disposeEdges?.();
        setEditorElement(current =>
          current === editorContainer ? null : current
        );
        disposableGroup.dispose();
      };
    },
    [focusTarget, mode, peekView, previewPage, shouldFocusPreview, xywh]
  );

  useEffect(() => {
    if (!editorElement || !shouldFocusPreview) {
      return;
    }
    syncPreviewPdfNavigation(editorElement, focusTarget);
    requestAnimationFrame(() =>
      focusOrFitPreviewTarget(editorElement, focusTarget, xywh, mode)
    );
  }, [editorElement, focusKey, focusTarget, mode, shouldFocusPreview, xywh]);

  useEffect(() => {
    if (
      !editorElement ||
      !focusTarget.fitToContent ||
      (focusTarget.mode ?? mode) !== 'edgeless'
    ) {
      return;
    }

    // Edgeless children hydrate after the editor-ready callback. A single
    // requestAnimationFrame can therefore fit an incomplete surface (for
    // example only the database corner that mounted first). Refit the
    // document-level result as its blocks arrive and once more after the
    // initial layout has gone quiet. Concrete hit previews never enter this
    // path; they remain anchored to their own block/element.
    let quietTimer: number | null = null;
    let fitPasses = 0;
    const refit = () => fitViewport(editorElement, xywh);
    const scheduleQuietRefit = () => {
      if (quietTimer !== null) window.clearTimeout(quietTimer);
      quietTimer = window.setTimeout(refit, 80);
    };
    const blockUpdates =
      editorElement.host?.std.store.slots.blockUpdated.subscribe(
        scheduleQuietRefit
      );
    // Layer registration is asynchronous but does not mutate the readonly
    // store, so `blockUpdated` alone cannot observe it. Keep the fit live
    // through the bounded initial hydration window.
    const hydrationFitTimer = window.setInterval(() => {
      refit();
      fitPasses += 1;
      if (fitPasses >= 30) window.clearInterval(hydrationFitTimer);
    }, 100);

    scheduleQuietRefit();
    return () => {
      blockUpdates?.unsubscribe();
      if (quietTimer !== null) window.clearTimeout(quietTimer);
      window.clearInterval(hydrationFitTimer);
    };
  }, [editorElement, focusKey, focusTarget, mode, xywh]);

  useEffect(() => {
    const normalizedQuery = searchQuery?.trim();
    if (!editorElement || !normalizedQuery) {
      return;
    }

    let cleanup = clearPreviewSearchHighlight;
    let cancelled = false;
    const frame = requestAnimationFrame(() => {
      if (cancelled) return;
      cleanup = applyPreviewSearchHighlight(
        editorElement,
        focusTarget,
        normalizedQuery
      );
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      cleanup();
    };
  }, [editorElement, focusKey, focusTarget, searchQuery]);

  useEffect(() => {
    const disposables: Subscription[] = [];
    const openHandler = (params: AIChatParams | null) => {
      if (!params) {
        return;
      }
      if (doc) {
        workbench.openDoc(doc.id);
        peekView.close();
        // chat panel open is already handled in <DetailPageImpl />
      }
    };
    disposables.push(
      AIProvider.slots.requestOpenWithChat.subscribe(openHandler)
    );
    disposables.push(
      AIProvider.slots.requestSendWithChat.subscribe(openHandler)
    );
    return () => disposables.forEach(d => d.unsubscribe());
  }, [doc, peekView, workbench, workspace.id]);

  const openOutlinePanel = useCallback(() => {
    workbench.openDoc(doc.id);
    workbench.openSidebar();
    workbench.activeView$.value.activeSidebarTab('outline');
    peekView.close();
  }, [doc, peekView, workbench]);

  return (
    <AffineErrorBoundary>
      <Scrollable.Root>
        <Scrollable.Viewport
          className={clsx('affine-page-viewport', styles.affineDocViewport)}
        >
          <Suspense fallback={<PageDetailLoading />}>
            <BlockSuiteEditor
              className={styles.editor}
              data-streamlain-peek-preview="true"
              mode={mode}
              page={previewPage}
              readonly={true}
              hideReadOnlyPermissionBanner={hideReadOnlyPermissionBanner}
              onEditorReady={handleOnEditorReady}
              defaultOpenProperty={defaultOpenProperty}
            />
          </Suspense>
        </Scrollable.Viewport>
        <Scrollable.Scrollbar />
      </Scrollable.Root>
      {!BUILD_CONFIG.isMobileEdition && !BUILD_CONFIG.isMobileWeb ? (
        <EditorOutlineViewer
          editor={editorElement?.host ?? null}
          show={mode === 'page'}
          openOutlinePanel={openOutlinePanel}
        />
      ) : null}
    </AffineErrorBoundary>
  );
}

export function DocPeekPreview({
  docRef,
  animating,
  hideReadOnlyPermissionBanner,
}: {
  docRef: DocReferenceInfo;
  animating?: boolean;
  hideReadOnlyPermissionBanner?: boolean;
}) {
  const {
    docId,
    blockIds,
    elementIds,
    parentBlockId,
    parentFlavour,
    noteBlockId,
    mode,
    pageIndex,
    query,
    requestId,
    xywh,
    fitToContent,
    databaseId,
    databaseDocId,
    databaseRowId,
  } = docRef;
  const blockKey = blockIds?.join(',') ?? '';
  const elementKey = elementIds?.join(',') ?? '';
  const focusTarget = useMemo(
    () => ({
      blockIds,
      elementIds,
      parentBlockId,
      parentFlavour,
      noteBlockId,
      mode,
      pageIndex,
      query,
      requestId,
      xywh,
      fitToContent,
      refreshKey: [
        docId,
        blockKey,
        elementKey,
        parentBlockId ?? '',
        parentFlavour ?? '',
        noteBlockId ?? '',
        databaseId ?? '',
        databaseRowId ?? '',
        pageIndex ?? '',
        query ?? '',
        requestId ?? '',
      ].join('|'),
    }),
    [
      blockIds,
      blockKey,
      docId,
      elementIds,
      elementKey,
      parentBlockId,
      parentFlavour,
      noteBlockId,
      databaseId,
      databaseRowId,
      mode,
      pageIndex,
      query,
      requestId,
      xywh,
      fitToContent,
    ]
  );
  const defaultOpenProperty = useMemo(
    () =>
      databaseId && databaseRowId && databaseDocId
        ? {
            docId: databaseDocId,
            databaseId,
            databaseRowId,
            type: 'database' as const,
          }
        : undefined,
    [databaseDocId, databaseId, databaseRowId]
  );
  const { doc, editor, loading } = useEditor(
    docId,
    mode,
    undefined,
    defaultOpenProperty,
    !animating
  );

  const canAccess = useGuard('Doc_Read', docId);

  // if sync engine has been synced and the page is null, show 404 page.
  if (!doc || !editor || !canAccess) {
    return loading || canAccess === undefined ? (
      <PageDetailLoading key="current-page-is-null" />
    ) : (
      <PageNotFound noPermission />
    );
  }

  return (
    <FrameworkScope scope={doc.scope}>
      <FrameworkScope scope={editor.scope}>
        <DocPeekPreviewEditor
          focusTarget={focusTarget}
          hideReadOnlyPermissionBanner={hideReadOnlyPermissionBanner}
          searchQuery={query}
          xywh={xywh}
        />
      </FrameworkScope>
    </FrameworkScope>
  );
}
